import { useQuery } from '@tanstack/react-query';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useHomepagePageData } from '#src/app/pages/homepage/pageContext';
import type {
	HomepageFeaturedTopicsFragment,
	HomepagePetitionFragment,
	TrendingSectionData,
} from '#src/app/pages/homepage/shared/api';
import { getTrendingSectionData } from '#src/app/pages/homepage/shared/api';
import { useCountryCode, useLocale } from '#src/app/shared/hooks/l10n';
import { useMappedLoadedState } from '#src/app/shared/hooks/state';
import type { State } from '#src/app/shared/utils/async';

import { useTrackAllTopicsClick } from '../useTrackAllTopicsClick';

type Tag = NonNullable<HomepagePetitionFragment['tagsConnection']>['nodes'][number];
type Topic = { name: string; slug: string };

type PetitionCardTrackingParams = {
	ask: string;
	petitionId: string;
	signatureCount: number;
	tags: readonly Tag[];
	cardPosition: number;
	location: string;
};

/** @knip-ignore */
export type TrendingData = {
	shouldShowSection: boolean;
	petitions: TrendingSectionData['trendingPetitions'];
	topics: Topic[];
};

type Result = ModelHookResult<
	{
		trendingData: State<{ value: TrendingData }>;
	},
	{
		onTopicClick: (topicName: string) => void;
		onAllTopicsClick: () => void;
		onPetitionCardClick: (trackingParams: PetitionCardTrackingParams) => void;
	}
>;

export function useTrendingSection(): Result {
	const track = useTracking();
	const trackAllTopicsClick = useTrackAllTopicsClick();
	const locale = useLocale();
	const countryCode = useCountryCode();
	const utilityContext = useUtilityContext();
	const {
		data: { trendingSectionData: prefetchedData },
	} = useHomepagePageData();

	const queryState = useQuery({
		queryKey: [getTrendingSectionData.queryKey, countryCode, locale],
		queryFn: async () => getTrendingSectionData(utilityContext, { countryCode, locale }),
		// page context only prefetches this for bots, so the section can be server-rendered
		// for them; with the data already in hand there is nothing to refetch. refetchOnMount
		// has to be overridden too, since the global default of 'always' ignores staleTime
		initialData: prefetchedData,
		...(prefetchedData && { staleTime: Infinity, refetchOnMount: false }),
	});

	const trendingData = useMappedLoadedState(queryState, ({ data }) => {
		const topics = data.trendingTopics.filter(
			(topic): topic is HomepageFeaturedTopicsFragment & Topic => topic.name !== null && topic.slug !== null,
		);
		return {
			value: {
				shouldShowSection: data.trendingPetitions.length > 0,
				petitions: data.trendingPetitions,
				topics,
			},
		};
	});

	return {
		actions: {
			onTopicClick: (topicName: string) => {
				void track('homepage_topic_page_click', { topic_name: topicName });
			},
			onAllTopicsClick: trackAllTopicsClick,
			onPetitionCardClick: (trackingParams: PetitionCardTrackingParams) =>
				// eslint-disable-next-line no-void
				void track('homepage_featured_petition_click', {
					petition_title: trackingParams.ask,
					petition_id: trackingParams.petitionId,
					signatures: trackingParams.signatureCount,
					tags: trackingParams.tags.map((tag) => tag.name),
					card_position: trackingParams.cardPosition,
					location: trackingParams.location,
				}),
		},
		data: { trendingData },
	};
}
